import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { VioTablesV2Component } from 'vio-tables-v2';
import type { VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { ApiRequestsService } from '../../services/api-requests.service';

interface Item {
    _id: string;
    itemCode: string;
    title: string;
    description: string;
    imageUrl: string;
}

interface TemplateCard {
    file: File | null;
    preview: string;
    bodyText: string;
    buttonNames: string[];
    useItem: boolean;
    itemId: string;
    itemTitle: string;
    itemImageUrl: string;
}

interface TemplateRecord {
    _id: string;
    metaTemplateId: string;
    templateName: string;
    category: string;
    language: string;
    templateType: 'standard' | 'engage';
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    rejectedReason: string | null;
    cardCount: number;
    createdAt: string;
}

@Component({
    selector: 'app-template-creation',
    standalone: true,
    imports: [CommonModule, FormsModule, VioTablesV2Component],
    templateUrl: './template-creation.component.html',
    styleUrl: './template-creation.component.scss',
})
export class TemplateCreationComponent implements OnInit, AfterViewInit, OnDestroy {
    // Rich text editor refs
    @ViewChild('textEditor') textEditorRef?: ElementRef<HTMLElement>;
    @ViewChild('imgEditor') imgEditorRef?: ElementRef<HTMLElement>;
    @ViewChild('outerEditor') outerEditorRef?: ElementRef<HTMLElement>;
    @ViewChildren('cardEditor') cardEditorRefs!: QueryList<ElementRef<HTMLElement>>;

    private _initializedEditors = new WeakSet<HTMLElement>();
    private _cardEditorSub?: Subscription;

    // Builder visibility
    showBuilderForm = false;
    showRejectionTips = false;

    // Format selector
    tplFormat: 'text' | 'image' | 'carousel' = 'carousel';

    // Shared meta fields
    tplName = '';
    tplCategory = 'MARKETING';
    tplLanguage = 'en';
    tplType: 'standard' | 'engage' = 'standard';
    tplButtonNames: string[] = ['View'];
    tplHasButtons = true;

    // Text / Image format state
    tplSingleBodyText = 'Hello [variable 1], check out our exclusive offer!';
    tplSingleFile: File | null = null;
    tplSinglePreview = '';

    // Carousel format state
    tplSameButtonsForAll = true;
    tplOuterBodyText = 'Hello! [variable 1] — Team Vintech';
    tplCards: TemplateCard[] = [
        { file: null, preview: '', bodyText: '✨ [variable 1] — Vintech Electronic Systems', buttonNames: ['View'], useItem: false, itemId: '', itemTitle: '', itemImageUrl: '' },
        { file: null, preview: '', bodyText: '✨ [variable 1] — Vintech Electronic Systems', buttonNames: ['View'], useItem: false, itemId: '', itemTitle: '', itemImageUrl: '' },
    ];

    // Item master
    items: Item[] = [];
    isLoadingItems = false;
    isCreatingTemplate = false;
    templateResult = '';
    templateResultSuccess = false;

    // Templates table
    templates: TemplateRecord[] = [];
    templatesForTable: any[] = [];
    isLoadingTemplates = false;

    // Auto-refresh state
    isAutoRefreshing = false;
    autoRefreshCountdown = 60;
    private _autoRefreshInterval: ReturnType<typeof setInterval> | null = null;
    private _countdownInterval: ReturnType<typeof setInterval> | null = null;

    templatesTableConfig: VT2_TABLE_CONFIG = {
        tableTitle: '',
        columnLabels: {
            templateName: 'Template Name',
            type: 'Type',
            cards: 'Cards',
            category: 'Category',
            language: 'Language',
            status: 'Status',
            created: 'Created',
        },
        excludeFields: ['_id', 'metaTemplateId'],
        isSortable: true,
        showFormulaButton: false,
        showSummationButton: false,
        showHeaderGroupButton: false,
        dateFields: ['created'],
        statusField: 'status',
        statusClassMap: {
            PENDING: 'bg-warning text-dark',
            APPROVED: 'bg-success',
            REJECTED: 'bg-danger',
        },
    };

    constructor(private apiService: ApiRequestsService) {}

    ngOnInit(): void {
        this.templatesTableConfig = {
            ...this.templatesTableConfig,
            actionButtons: [
                {
                    icon: '🔄',
                    label: 'Refresh',
                    title: 'Refresh status from Meta',
                    onClick: (row) => this.refreshTemplateStatus(row),
                },
            ],
        };
        this.loadTemplates();
        this.loadItems();
    }

    ngAfterViewInit(): void {
        this._cardEditorSub = this.cardEditorRefs.changes.subscribe((list: QueryList<ElementRef<HTMLElement>>) => {
            list.forEach((ref, i) => {
                if (this.tplCards[i]) {
                    this.initEditorOnce(ref.nativeElement, this.tplCards[i].bodyText);
                }
            });
        });
    }

    ngOnDestroy(): void {
        this.stopAutoRefresh();
        this._cardEditorSub?.unsubscribe();
    }

    trackByIndex(index: number): number { return index; }

    // ── Rich text conversion ─────────────────────────────────────────────────────

    /** WA markdown → safe HTML for display in preview bubbles */
    previewHtml(md: string): string {
        if (!md) return '';
        return md
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
            .replace(/_([^_\n]+)_/g, '<em>$1</em>')
            .replace(/~([^~\n]+)~/g, '<s>$1</s>')
            .replace(/\[variable (\d+)\]/gi, '<span class="wa-var">[variable $1]</span>')
            .replace(/\n/g, '<br>');
    }

    /** WA markdown → HTML to set as contenteditable innerHTML */
    waMarkdownToHtml(md: string): string {
        if (!md) return '';
        return md
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
            .replace(/_([^_\n]+)_/g, '<em>$1</em>')
            .replace(/~([^~\n]+)~/g, '<s>$1</s>')
            .replace(/\[variable (\d+)\]/gi, '<span class="var-chip">[variable $1]</span>')
            .replace(/\n/g, '<br>');
    }

    /** contenteditable innerHTML → WA markdown for model/backend */
    htmlToWaMarkdown(html: string): string {
        if (!html) return '';
        const processed = html
            .replace(/<strong[^>]*>([\s\S]*?)<\/strong>/gi, '\x01$1\x01')
            .replace(/<b[^>]*>([\s\S]*?)<\/b>/gi, '\x01$1\x01')
            .replace(/<em[^>]*>([\s\S]*?)<\/em>/gi, '\x02$1\x02')
            .replace(/<i[^>]*>([\s\S]*?)<\/i>/gi, '\x02$1\x02')
            .replace(/<s[^>]*>([\s\S]*?)<\/s>/gi, '\x03$1\x03')
            .replace(/<strike[^>]*>([\s\S]*?)<\/strike>/gi, '\x03$1\x03')
            .replace(/<del[^>]*>([\s\S]*?)<\/del>/gi, '\x03$1\x03')
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/div>/gi, '\n')
            .replace(/<div[^>]*>/gi, '');
        const tmp = document.createElement('div');
        tmp.innerHTML = processed;
        return (tmp.textContent || '')
            // Trim stray whitespace from inside each formatting pair before converting to WA markers.
            // e.g. "\x01 Dipak\x01" → "\x01Dipak\x01" → "*Dipak*" (WhatsApp won't bold "* Dipak*")
            .replace(/\x01\s*([\s\S]*?)\s*\x01/g, (_, inner) => `\x01${inner.trim()}\x01`)
            .replace(/\x02\s*([\s\S]*?)\s*\x02/g, (_, inner) => `\x02${inner.trim()}\x02`)
            .replace(/\x03\s*([\s\S]*?)\s*\x03/g, (_, inner) => `\x03${inner.trim()}\x03`)
            .replace(/\x01/g, '*')
            .replace(/\x02/g, '_')
            .replace(/\x03/g, '~')
            .replace(/\n{3,}/g, '\n\n')
            .trim();
    }

    // ── Rich text editor methods ──────────────────────────────────────────────────

    applyFmtRich(cmd: 'bold' | 'italic' | 'strikeThrough'): void {
        document.execCommand(cmd, false);
        const el = document.activeElement as HTMLElement;
        if (el?.isContentEditable) {
            el.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }

    insertVarRich(el: HTMLElement): void {
        el.focus();
        const text = el.innerText || '';
        const existing = [...text.matchAll(/\[variable (\d+)\]/gi)].map(m => parseInt(m[1]));
        const n = existing.length ? Math.max(...existing) + 1 : 1;
        document.execCommand('insertText', false, `[variable ${n}]`);
        el.dispatchEvent(new Event('input', { bubbles: true }));
    }

    onBodyInput(event: Event, target: 'single' | 'outer' | 'card', cardIndex?: number): void {
        const el = event.target as HTMLElement;
        const md = this.htmlToWaMarkdown(el.innerHTML);
        if (target === 'single') this.tplSingleBodyText = md;
        else if (target === 'outer') this.tplOuterBodyText = md;
        else if (target === 'card' && cardIndex !== undefined) this.tplCards[cardIndex].bodyText = md;
    }

    initEditorOnce(el: HTMLElement | undefined, md: string): void {
        if (!el || this._initializedEditors.has(el)) return;
        this._initializedEditors.add(el);
        el.innerHTML = this.waMarkdownToHtml(md);
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
    }

    private initVisibleEditors(): void {
        if (this.textEditorRef?.nativeElement) {
            this.initEditorOnce(this.textEditorRef.nativeElement, this.tplSingleBodyText);
        }
        if (this.imgEditorRef?.nativeElement) {
            this.initEditorOnce(this.imgEditorRef.nativeElement, this.tplSingleBodyText);
        }
        if (this.outerEditorRef?.nativeElement) {
            this.initEditorOnce(this.outerEditorRef.nativeElement, this.tplOuterBodyText);
        }
        this.cardEditorRefs?.forEach((ref, i) => {
            if (this.tplCards[i]) {
                this.initEditorOnce(ref.nativeElement, this.tplCards[i].bodyText);
            }
        });
    }

    // ── Form methods ──────────────────────────────────────────────────────────────

    toggleBuilderForm(): void {
        this.showBuilderForm = !this.showBuilderForm;
        if (this.showBuilderForm) {
            this.templateResult = '';
            Promise.resolve().then(() => this.initVisibleEditors());
        }
    }

    normalizeTplName(): void {
        this.tplName = this.tplName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    }

    get tplNameError(): string {
        if (!this.tplName) return '';
        return /^[a-z0-9_]+$/.test(this.tplName) ? '' : 'Only lowercase letters, numbers, and underscores.';
    }

    setTplFormat(fmt: 'text' | 'image' | 'carousel'): void {
        this.tplFormat = fmt;
        this.tplSingleFile = null;
        this.tplSinglePreview = '';
        this.templateResult = '';
        Promise.resolve().then(() => this.initVisibleEditors());
    }

    setTplType(type: 'standard' | 'engage'): void {
        this.tplType = type;
        this.tplButtonNames = type === 'standard' ? ['View'] : ['Interested', 'Not Interested'];
        this.tplCards.forEach(c => { c.buttonNames = [...this.tplButtonNames]; });
    }

    toggleSameButtonsForAll(): void {
        this.tplSameButtonsForAll = !this.tplSameButtonsForAll;
        if (!this.tplSameButtonsForAll) {
            this.tplCards.forEach(c => { c.buttonNames = [...this.tplButtonNames]; });
        }
    }

    triggerSingleFileInput(): void {
        (document.getElementById('tpl_single_img') as HTMLInputElement)?.click();
    }

    onSingleFileSelect(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { alert('Please select an image file.'); input.value = ''; return; }
        this.tplSingleFile = file;
        const reader = new FileReader();
        reader.onload = e => { this.tplSinglePreview = e.target?.result as string; };
        reader.readAsDataURL(file);
    }

    previewButtonNames(i: number): string[] {
        return this.tplSameButtonsForAll ? this.tplButtonNames : this.tplCards[i].buttonNames;
    }

    // ── Validation ──────────────────────────────────────────────────────────────

    validateBodyText(text: string): { errors: string[]; warnings: string[] } {
        const errors: string[] = [];
        const warnings: string[] = [];
        if (!text.trim()) return { errors, warnings };

        const indices = [...text.matchAll(/\[variable (\d+)\]/gi)].map(m => parseInt(m[1]));
        const unique = [...new Set(indices)].sort((a, b) => a - b);

        if (unique.length > 0 && unique[0] !== 1) {
            errors.push(`Variables must start at [variable 1]. Lowest found: [variable ${unique[0]}].`);
        }
        for (let i = 1; i < unique.length; i++) {
            if (unique[i] !== unique[i - 1] + 1) {
                errors.push(`Variable gap: [variable ${unique[i-1]}] jumps to [variable ${unique[i]}]. Missing [variable ${unique[i-1]+1}].`);
                break;
            }
        }
        const seen = new Set<number>(); const dups: number[] = [];
        indices.forEach(n => { if (seen.has(n)) dups.push(n); else seen.add(n); });
        if (dups.length) {
            errors.push(`Duplicate variable(s): ${[...new Set(dups)].map(n => `[variable ${n}]`).join(', ')} used more than once.`);
        }

        const varCount = indices.length;
        if (varCount > 0) {
            const wordCount = text.replace(/\[variable \d+\]/gi, '').trim().split(/\s+/).filter(w => w.length > 0).length;
            if (wordCount < varCount * 3) {
                errors.push(`Too few words for ${varCount} variable(s). Meta needs ~3 surrounding words per variable.`);
            }
        }

        if (/https?:\/\/|www\./i.test(text)) {
            warnings.push('URLs in body text are often rejected by Meta. Use a button for links instead.');
        }
        if (/[!?]{3,}/.test(text)) {
            warnings.push('Excessive punctuation (!!!, ???) looks spammy to Meta\'s automated review.');
        }
        const capsWords = (text.match(/\b[A-Z]{4,}\b/g) || []).filter(w => !/\[variable/i.test(w));
        if (capsWords.length > 0) {
            warnings.push(`ALL CAPS words (${capsWords.slice(0, 2).join(', ')}) may be flagged as spam.`);
        }
        const PROMO = ['\\bfree\\b', '\\bwin\\b', '\\bwinner\\b', '\\bguaranteed\\b', 'click here', 'act now', 'limited time offer'];
        if (PROMO.some(p => new RegExp(p, 'i').test(text))) {
            warnings.push('Promotional/spam language detected. Meta may reject this template.');
        }

        return { errors, warnings };
    }

    bodyErrors(text: string): string[] { return this.validateBodyText(text).errors; }
    bodyWarnings(text: string): string[] { return this.validateBodyText(text).warnings; }

    bodyVarWarning(text: string): string {
        return this.bodyErrors(text).find(e => e.includes('Too few words')) || '';
    }

    get tplNameWarnings(): string[] {
        if (!this.tplName) return [];
        return /\b(test|temp|demo|sample|draft)\b/i.test(this.tplName)
            ? ['Template name contains test/temp/demo — Meta may treat this as non-production content.']
            : [];
    }

    buttonErrors(name: string): string[] {
        const errs: string[] = [];
        if (/\{\{/.test(name)) errs.push('Button text cannot contain {{variables}}.');
        return errs;
    }

    get allFormErrors(): string[] {
        const errs: string[] = [];
        if (this.tplFormat === 'text' || this.tplFormat === 'image') {
            this.bodyErrors(this.tplSingleBodyText).forEach(e => errs.push(`Body: ${e}`));
        } else {
            this.bodyErrors(this.tplOuterBodyText).forEach(e => errs.push(`Outer body: ${e}`));
            this.tplCards.forEach((c, i) => this.bodyErrors(c.bodyText).forEach(e => errs.push(`Card ${i + 1}: ${e}`)));
        }
        const btns = this.tplFormat === 'carousel' && !this.tplSameButtonsForAll
            ? this.tplCards.flatMap(c => c.buttonNames)
            : (this.tplHasButtons ? this.tplButtonNames : []);
        btns.forEach(b => this.buttonErrors(b).forEach(e => errs.push(`Button "${b}": ${e}`)));
        return errs;
    }

    get allFormWarnings(): string[] {
        const warns: string[] = [];
        warns.push(...this.tplNameWarnings);
        if (this.tplFormat === 'text' || this.tplFormat === 'image') {
            this.bodyWarnings(this.tplSingleBodyText).forEach(w => warns.push(`Body: ${w}`));
        } else {
            this.bodyWarnings(this.tplOuterBodyText).forEach(w => warns.push(`Outer body: ${w}`));
            this.tplCards.forEach((c, i) => this.bodyWarnings(c.bodyText).forEach(w => warns.push(`Card ${i + 1}: ${w}`)));
        }
        return warns;
    }

    get tplIsValid(): boolean {
        if (!this.tplName || !!this.tplNameError) return false;
        const btnsOk = !this.tplHasButtons || (this.tplButtonNames.length > 0 && this.tplButtonNames.every(b => b.trim() !== '' && this.buttonErrors(b).length === 0));

        if (this.tplFormat === 'text') {
            if (!this.tplSingleBodyText.trim()) return false;
            if (this.bodyErrors(this.tplSingleBodyText).length > 0) return false;
            return btnsOk;
        }
        if (this.tplFormat === 'image') {
            if (!this.tplSingleBodyText.trim()) return false;
            if (this.bodyErrors(this.tplSingleBodyText).length > 0) return false;
            if (!this.tplSingleFile) return false;
            return btnsOk;
        }
        if (!this.tplOuterBodyText.trim()) return false;
        if (this.bodyErrors(this.tplOuterBodyText).length > 0) return false;
        if (this.tplCards.some(c => this.bodyErrors(c.bodyText).length > 0)) return false;
        if (this.tplCards.length < 2) return false;
        if (!this.tplCards.every(c => c.bodyText.trim() !== '' && (c.useItem ? !!c.itemId : c.file !== null))) return false;
        const carouselBtns = this.tplSameButtonsForAll ? this.tplButtonNames : this.tplCards.flatMap(c => c.buttonNames);
        if (carouselBtns.some(b => !b.trim() || this.buttonErrors(b).length > 0)) return false;
        if (this.tplSameButtonsForAll) return this.tplButtonNames.length > 0;
        return this.tplCards.every(c => c.buttonNames.length > 0);
    }

    loadItems(): void {
        this.isLoadingItems = true;
        this.apiService.getApiCall('api/v1/items').subscribe({
            next: (res: any) => {
                this.items = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
                this.isLoadingItems = false;
            },
            error: () => { this.isLoadingItems = false; },
        });
    }

    toggleItemLink(i: number): void {
        const card = this.tplCards[i];
        card.useItem = !card.useItem;
        if (!card.useItem) {
            card.itemId = '';
            card.itemTitle = '';
            card.itemImageUrl = '';
            card.preview = card.file ? card.preview : '';
        } else {
            card.file = null;
            card.preview = '';
        }
    }

    onItemSelect(i: number, itemId: string): void {
        if (!itemId) return;
        const item = this.items.find(it => String(it._id) === String(itemId));
        if (!item) return;
        const card = this.tplCards[i];
        card.itemId = String(item._id);
        card.itemTitle = item.title;
        card.itemImageUrl = item.imageUrl || '';
        card.preview = item.imageUrl || '';
        if (!card.bodyText.trim() || card.bodyText.includes('Vintech Electronic Systems')) {
            card.bodyText = item.description || `📖 ${item.title}`;
        }
        // Update editor HTML for this card if already initialized
        const cardEditorEl = this.cardEditorRefs?.get(i)?.nativeElement;
        if (cardEditorEl) {
            this._initializedEditors.delete(cardEditorEl);
            this.initEditorOnce(cardEditorEl, card.bodyText);
        }
    }

    addTplCard(): void {
        if (this.tplCards.length < 10) {
            this.tplCards.push({
                file: null,
                preview: '',
                bodyText: '✨ [variable 1] — Vintech Electronic Systems',
                buttonNames: [...this.tplButtonNames],
                useItem: false,
                itemId: '',
                itemTitle: '',
                itemImageUrl: '',
            });
            // cardEditorRefs.changes subscription handles init of the new editor
        }
    }

    removeTplCard(i: number): void {
        if (this.tplCards.length > 2) {
            this.tplCards.splice(i, 1);
            // Force reinit of all card editors after DOM updates
            Promise.resolve().then(() => {
                this.cardEditorRefs?.forEach((ref, idx) => {
                    this._initializedEditors.delete(ref.nativeElement);
                    if (this.tplCards[idx]) {
                        this.initEditorOnce(ref.nativeElement, this.tplCards[idx].bodyText);
                    }
                });
            });
        }
    }

    triggerTplFileInput(i: number): void {
        (document.getElementById(`tpl_card_img_${i}`) as HTMLInputElement)?.click();
    }

    onTplFileSelect(event: Event, i: number): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { alert('Please select an image file.'); input.value = ''; return; }
        this.tplCards[i].file = file;
        const reader = new FileReader();
        reader.onload = e => { this.tplCards[i].preview = e.target?.result as string; };
        reader.readAsDataURL(file);
    }

    createTemplate(): void {
        if (!this.tplIsValid || this.isCreatingTemplate) return;
        this.isCreatingTemplate = true;
        this.templateResult = '';

        const fd = new FormData();
        fd.append('templateName', this.tplName);
        fd.append('category', this.tplCategory);
        fd.append('language', this.tplLanguage);
        fd.append('templateType', this.tplType);
        fd.append('templateFormat', this.tplFormat);

        const encodeVars = (t: string) => t.replace(/\[variable (\d+)\]/gi, '{{$1}}');

        if (this.tplFormat === 'text') {
            fd.append('bodyText', encodeVars(this.tplSingleBodyText.trim()));
            fd.append('hasButtons', String(this.tplHasButtons));
            if (this.tplHasButtons) fd.append('buttonNames', JSON.stringify(this.tplButtonNames.map(b => b.trim())));

        } else if (this.tplFormat === 'image') {
            fd.append('bodyText', encodeVars(this.tplSingleBodyText.trim()));
            fd.append('hasButtons', String(this.tplHasButtons));
            if (this.tplHasButtons) fd.append('buttonNames', JSON.stringify(this.tplButtonNames.map(b => b.trim())));
            fd.append('images', this.tplSingleFile!, this.tplSingleFile!.name);

        } else {
            fd.append('outerBodyText', encodeVars(this.tplOuterBodyText.trim()));
            fd.append('cardBodyTexts', JSON.stringify(this.tplCards.map(c => encodeVars(c.bodyText.trim()))));
            if (this.tplSameButtonsForAll) {
                fd.append('buttonNames', JSON.stringify(this.tplButtonNames.map(b => b.trim())));
            } else {
                fd.append('cardButtonNames', JSON.stringify(this.tplCards.map(c => c.buttonNames.map(b => b.trim()))));
            }
            fd.append('cardItems', JSON.stringify(this.tplCards.map(c => c.useItem ? c.itemId : null)));
            this.tplCards.filter(c => !c.useItem).forEach(card => fd.append('images', card.file!, card.file!.name));
        }

        this.apiService.uploadMultiple(fd, 'api/v1/campaign/templates').subscribe({
            next: (res: any) => {
                this.isCreatingTemplate = false;
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    const { metaTemplateId, status } = res.Data;
                    this.templateResult = `Template submitted! Meta ID: ${metaTemplateId} · Status: ${status}`;
                    this.templateResultSuccess = true;
                    this.showBuilderForm = false;
                    this.loadTemplates();
                } else {
                    this.templateResult = res?.errorDescription || 'Failed to create template.';
                    this.templateResultSuccess = false;
                }
            },
            error: () => {
                this.isCreatingTemplate = false;
                this.templateResult = 'Failed to create template. Please check your connection.';
                this.templateResultSuccess = false;
            },
        });
    }

    loadTemplates(): void {
        this.isLoadingTemplates = true;
        this.apiService.getApiCall('api/v1/campaign/templates').subscribe({
            next: (res: any) => {
                this.isLoadingTemplates = false;
                this.templates = res?.errorCode === this.apiService.NO_ERROR ? (res.Data || []) : [];
                this.templatesForTable = this.buildTemplatesForTable();
                this.syncAutoRefresh();
            },
            error: () => { this.isLoadingTemplates = false; },
        });
    }

    refreshTemplateStatus(row: any): void {
        this.apiService.getApiCall(`api/v1/campaign/templates/${row.metaTemplateId}/status`).subscribe({
            next: (res: any) => {
                if (res?.errorCode === this.apiService.NO_ERROR) {
                    const t = this.templates.find(t => t.metaTemplateId === row.metaTemplateId);
                    if (t) {
                        t.status = res.Data.status;
                        t.rejectedReason = res.Data.rejectedReason;
                    }
                    this.templatesForTable = this.buildTemplatesForTable();
                    this.syncAutoRefresh();
                }
            },
            error: () => {},
        });
    }

    get hasPendingTemplates(): boolean {
        return this.templates.some(t => t.status === 'PENDING');
    }

    private syncAutoRefresh(): void {
        if (this.hasPendingTemplates) {
            this.startAutoRefresh();
        } else {
            this.stopAutoRefresh();
        }
    }

    private startAutoRefresh(): void {
        if (this._autoRefreshInterval) return;
        this.isAutoRefreshing = true;
        this.autoRefreshCountdown = 60;

        this._countdownInterval = setInterval(() => {
            this.autoRefreshCountdown--;
        }, 1000);

        this._autoRefreshInterval = setInterval(() => {
            this.autoRefreshCountdown = 60;
            this.pollPendingTemplates();
        }, 60000);
    }

    private stopAutoRefresh(): void {
        if (this._autoRefreshInterval) { clearInterval(this._autoRefreshInterval); this._autoRefreshInterval = null; }
        if (this._countdownInterval) { clearInterval(this._countdownInterval); this._countdownInterval = null; }
        this.isAutoRefreshing = false;
    }

    private pollPendingTemplates(): void {
        const pending = this.templates.filter(t => t.status === 'PENDING');
        if (!pending.length) { this.stopAutoRefresh(); return; }

        pending.forEach(t => {
            this.apiService.getApiCall(`api/v1/campaign/templates/${t.metaTemplateId}/status`).subscribe({
                next: (res: any) => {
                    if (res?.errorCode === this.apiService.NO_ERROR && res.Data.status !== t.status) {
                        t.status = res.Data.status;
                        t.rejectedReason = res.Data.rejectedReason;
                        this.templatesForTable = this.buildTemplatesForTable();
                        if (!this.hasPendingTemplates) this.stopAutoRefresh();
                    }
                },
                error: () => {},
            });
        });
    }

    private buildTemplatesForTable(): any[] {
        return this.templates.map(t => ({
            _id: t._id,
            metaTemplateId: t.metaTemplateId,
            templateName: t.templateName,
            type: t.templateType,
            cards: t.cardCount,
            category: t.category,
            language: t.language,
            status: t.status,
            created: t.createdAt,
        }));
    }
}
